import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isValid, parseISO } from 'date-fns';
import { sl } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EditInvoiceModal from './EditInvoiceModal';
import { formatSlNumber } from '../utils/csvUtils';

const { FiList, FiEdit, FiTrash2, FiUser, FiEuro, FiClock, FiCalendar, FiPackage, FiCreditCard } = FiIcons;

const InvoiceList = ({ invoices, onDeleteInvoice, onEditInvoice, onMarkAsPaid }) => {
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [filterInterval, setFilterInterval] = useState('all');

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
  };

  const handleSaveEdit = (updatedInvoice) => {
    onEditInvoice(editingInvoice.id, updatedInvoice);
    setEditingInvoice(null);
  };

  const handleArchive = (invoiceId, clientName) => {
    if (window.confirm(`Ali ste prepričani, da želite arhivirati račun za ${clientName}?`)) {
      onDeleteInvoice(invoiceId);
    }
  };

  const handlePayment = (invoiceId, clientName) => {
    if (window.confirm(`Ali želite označiti račun za ${clientName} kot plačan?`)) {
      onMarkAsPaid(invoiceId);
    }
  };

  const getTotalAmount = (invoice) => {
    return invoice.total_amount || invoice.totalAmount || invoice.amount || 0;
  };

  const getIntervalFull = (interval) => {
    switch (interval) {
      case 'monthly': return 'Mesečno';
      case 'quarterly': return 'Četrtletno';
      case 'half-yearly': return 'Polletno';
      case 'yearly': return 'Letno';
      default: return 'Mesečno';
    }
  };

  // Safe date formatting function
  const formatSafeDate = (dateValue) => {
    try {
      if (!dateValue) return 'Ni datuma';
      
      let date;
      if (typeof dateValue === 'string') {
        date = parseISO(dateValue);
      } else {
        date = new Date(dateValue);
      }
      
      if (!isValid(date)) {
        console.warn('Invalid date:', dateValue);
        return 'Neveljaven datum';
      }
      
      return format(date, 'd.M.yyyy', { locale: sl });
    } catch (error) {
      console.error('Date formatting error:', error, 'for value:', dateValue);
      return 'Napaka datuma';
    }
  };

  // Filter invoices by interval
  const filteredInvoices = filterInterval === 'all' 
    ? invoices 
    : invoices.filter(invoice => invoice.interval === filterInterval);

  // Sort invoices: first by date, then alphabetically by customer name
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    try {
      const dateA = new Date(a.next_due || a.nextDue);
      const dateB = new Date(b.next_due || b.nextDue);
      
      // First sort by date (if both are valid)
      if (isValid(dateA) && isValid(dateB) && dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB;
      }
      
      // If dates are the same or invalid, sort alphabetically by customer name
      const nameA = (a.stranka || a.clientName || '').toLowerCase();
      const nameB = (b.stranka || b.clientName || '').toLowerCase();
      return nameA.localeCompare(nameB, 'sl');
    } catch (error) {
      console.error('Sorting error:', error);
      return 0;
    }
  });

  const intervals = [
    { value: 'all', label: 'Vsi' },
    { value: 'monthly', label: 'Mesečno' },
    { value: 'quarterly', label: 'Četrtletno' },
    { value: 'half-yearly', label: 'Polletno' },
    { value: 'yearly', label: 'Letno' }
  ];

  if (invoices.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-12"
      >
        <SafeIcon icon={FiList} className="text-5xl text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-[#4d504a] mb-1">Še ni računov</h3>
        <p className="text-gray-500 text-sm">Začnite z dodajanjem prvega računa</p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-4 mb-6 px-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-[#4d504a] mb-1">Vsi računi</h2>
          <p className="text-[#6e725c] text-sm">
            {filteredInvoices.length} od {invoices.length} račun{filteredInvoices.length === 1 ? '' : filteredInvoices.length === 2 ? 'a' : filteredInvoices.length <= 4 ? 'i' : 'ov'}
          </p>
        </div>

        {/* Horizontal Filter */}
        <div className="flex flex-wrap justify-center gap-2">
          {intervals.map(interval => (
            <motion.button
              key={interval.value}
              onClick={() => setFilterInterval(interval.value)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filterInterval === interval.value
                  ? 'bg-[#6e725c] text-white shadow-lg'
                  : 'bg-white text-[#4d504a] hover:bg-gray-100 shadow-md'
              }`}
            >
              {interval.label}
              {interval.value !== 'all' && (
                <span className="ml-1 text-xs opacity-75">
                  ({invoices.filter(inv => inv.interval === interval.value).length})
                </span>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {sortedInvoices.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12"
        >
          <SafeIcon icon={FiList} className="text-5xl text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-[#4d504a] mb-1">Ni zadetkov</h3>
          <p className="text-gray-500 text-sm">Za izbrani filter ni računov</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {sortedInvoices.map((invoice, index) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow border-l-4 border-l-[#6e725c] h-fit"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center min-w-0 flex-1">
                  <SafeIcon icon={FiUser} className="text-[#6e725c] mr-2 flex-shrink-0 text-sm" />
                  <h3 className="font-semibold text-[#4d504a] text-sm truncate">
                    {invoice.stranka || invoice.clientName}
                  </h3>
                  {invoice.line_items && invoice.line_items.length > 0 && (
                    <div className="ml-1 relative flex-shrink-0">
                      <SafeIcon icon={FiPackage} className="text-[#6e725c] text-xs" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center text-[9px]">
                        {invoice.line_items.length}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount */}
              <div className="flex items-center mb-3">
                <SafeIcon icon={FiEuro} className="text-[#80b238] mr-2 text-sm" />
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-[#80b238] text-lg block">
                    {formatSlNumber(getTotalAmount(invoice))} €
                  </span>
                </div>
              </div>

              {/* Info row */}
              <div className="flex items-center justify-between mb-3 text-xs">
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="text-[#6e725c] mr-1" />
                  <span className="text-[#4d504a]">{getIntervalFull(invoice.interval)}</span>
                </div>
                <div className="flex items-center">
                  <SafeIcon icon={FiCalendar} className="text-[#3297c7] mr-1" />
                  <span className="text-[#4d504a]">
                    {formatSafeDate(invoice.next_due || invoice.nextDue)}
                  </span>
                </div>
              </div>

              {/* Line items info */}
              {invoice.line_items && invoice.line_items.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs text-[#6e725c]">
                    +{invoice.line_items.length} postavk{invoice.line_items.length === 1 ? 'a' : invoice.line_items.length <= 4 ? 'e' : ''}
                  </span>
                </div>
              )}

              {/* Description */}
              {invoice.description && (
                <p className="text-[#4d504a] text-xs mb-3 line-clamp-2">{invoice.description}</p>
              )}

              {/* Action buttons */}
              <div className="space-y-2">
                {/* Payment button - full width */}
                <motion.button
                  onClick={() => handlePayment(invoice.id, invoice.stranka || invoice.clientName)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-[#80b238] text-white px-3 py-2 rounded text-xs font-medium hover:bg-[#6d9530] transition-colors flex items-center justify-center"
                  title="Zabeleži plačilo"
                >
                  <SafeIcon icon={FiCreditCard} className="mr-1 text-xs" />
                  Zabeleži plačilo
                </motion.button>

                {/* Edit and Delete buttons - side by side */}
                <div className="flex space-x-2">
                  <motion.button
                    onClick={() => handleEdit(invoice)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 bg-[#3297c7] text-white p-2 rounded hover:bg-[#2a7ca8] transition-colors flex items-center justify-center"
                    title="Uredi račun"
                  >
                    <SafeIcon icon={FiEdit} className="text-xs" />
                  </motion.button>
                  <motion.button
                    onClick={() => handleArchive(invoice.id, invoice.stranka || invoice.clientName)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex-1 bg-gray-400 text-white p-2 rounded hover:bg-red-500 transition-colors flex items-center justify-center"
                    title="Arhiviraj račun"
                  >
                    <SafeIcon icon={FiTrash2} className="text-xs" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editingInvoice && (
          <EditInvoiceModal
            invoice={editingInvoice}
            onSave={handleSaveEdit}
            onClose={() => setEditingInvoice(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceList;